import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetchClient } from "@/lib/api/client";
import {
  defaultLocale,
  getLocaleDirection,
  isSupportedLocale,
  keys,
  type PortalLocale,
} from "@/lib/i18n/portal";
import { useTranslations } from "@/lib/i18n/intl";
import { Surface } from "@/shared/layout/Surface";
import { endpoints } from "@/lib/api/endpoints";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { useStoreSwitch } from "@/shared/services/useStoreSwitch";
import type { SellerAuthSessionResponse } from "@/lib/api/types";

function getResolvedLocale(value: string | undefined): PortalLocale {
  return value && isSupportedLocale(value) ? value : defaultLocale;
}

type SessionState =
  | { status: "loading" }
  | { status: "ready"; data: SellerAuthSessionResponse };

/**
 * Post-login store picker. The seller is already authenticated here (JWT cookie
 * set at login); we fetch the session once — modeled on RootRedirectPage — to
 * read `availableStores` + the active `seller.storeSlug`.
 *
 * - Session 401/403 (or fetch failure) → redirect to `/{locale}/login`.
 * - While loading → the same loading Surface RootRedirectPage/ProtectedLayout use.
 * - `availableStores.length <= 1` → redirect (replace) to the active store. This
 *   is defensive: single-store sellers should not normally reach this page (the
 *   post-login branch in StoreLookupForm sends them straight to their store), so
 *   there is no visible picker for them even in the edge case they land here.
 * - `availableStores.length > 1` → render the selection list. Selecting the
 *   already-active store just navigates in; selecting a non-active store goes
 *   through `useStoreSwitch` (switch-then-navigate) — never a bare URL nav, which
 *   the ProtectedLayout guard would bounce.
 */
export function SelectStorePage() {
  const { locale: routeLocale } = useParams();
  const locale = getResolvedLocale(routeLocale);
  const navigate = useNavigate();
  const t = useTranslations();

  const [session, setSession] = useState<SessionState>({ status: "loading" });

  useEffect(() => {
    let isActive = true;

    const loadSession = async () => {
      try {
        const response = await apiFetchClient(endpoints.seller.auth.me, {
          cache: "no-store",
        });

        if (!response.ok) {
          if (isActive) {
            navigate(`/${locale}/login`, { replace: true });
          }
          return;
        }

        const data = (await response.json()) as SellerAuthSessionResponse;
        if (!isActive) {
          return;
        }

        const activeStoreSlug = data.seller?.storeSlug?.trim();
        const availableStores = data.availableStores ?? [];

        // Single-store (defensive) → straight to the active store, no picker.
        if (availableStores.length <= 1) {
          navigate(
            activeStoreSlug
              ? getSellerPath(locale, activeStoreSlug)
              : `/${locale}/login`,
            { replace: true },
          );
          return;
        }

        setSession({ status: "ready", data });
      } catch {
        if (isActive) {
          navigate(`/${locale}/login`, { replace: true });
        }
      }
    };

    void loadSession();

    return () => {
      isActive = false;
    };
  }, [locale, navigate]);

  if (session.status === "loading") {
    return (
      <Surface
        locale={locale}
        direction={getLocaleDirection(locale)}
        title={t(keys.common.loading)}
      >
        <div className="text-sm text-[color:var(--portal-muted)]">
          {t(keys.common.loading)}
        </div>
      </Surface>
    );
  }

  return (
    <SelectStoreList
      locale={locale}
      activeStoreSlug={session.data.seller?.storeSlug?.trim() ?? ""}
      availableStores={session.data.availableStores ?? []}
    />
  );
}

function SelectStoreList({
  locale,
  activeStoreSlug,
  availableStores,
}: {
  locale: PortalLocale;
  activeStoreSlug: string;
  availableStores: SellerAuthSessionResponse["availableStores"];
}) {
  const t = useTranslations();
  const navigate = useNavigate();
  const { isSwitching, error, switchTo } = useStoreSwitch(
    locale,
    activeStoreSlug,
  );

  const [creating, setCreating] = useState(false);
  const [newStoreName, setNewStoreName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const handleSelect = (slug: string) => {
    if (isSwitching) return;
    // Already the active store → no switch needed, just navigate in.
    if (slug === activeStoreSlug) {
      navigate(getSellerPath(locale, slug), { replace: true });
      return;
    }
    void switchTo(slug);
  };

  const handleCreate = async () => {
    const name = newStoreName.trim();
    if (name.length < 2 || isCreating) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      const response = await apiFetchClient(endpoints.seller.store, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storeName: name }),
      });
      if (!response.ok) {
        setCreateError(t(keys.auth.createStoreError));
        return;
      }
      const { storeSlug } = (await response.json()) as { storeSlug: string };
      // Switch into the store the seller just opened.
      await switchTo(storeSlug);
    } catch {
      setCreateError(t(keys.auth.createStoreError));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <main
      className="min-h-svh bg-[color:var(--surface)] px-6 py-12 sm:py-16"
      dir={getLocaleDirection(locale)}
      lang={locale}
    >
      <div className="mx-auto w-full max-w-[440px]">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--accent-600)]">
          {t(keys.auth.selectStoreEyebrow)}
        </div>
        <h1 className="m-0 text-[26px] font-semibold leading-tight tracking-[-0.01em] text-[color:var(--text-primary)]">
          {t(keys.auth.selectStoreTitle)}
        </h1>
        <p className="mb-7 mt-1.5 text-[13.5px] text-[color:var(--text-muted)]">
          {t(keys.auth.selectStoreSubtitle)}
        </p>

        <ul className="flex flex-col gap-2.5" role="list">
          {(availableStores ?? []).map((store) => {
            const isActive = store.storeSlug === activeStoreSlug;
            return (
              <li key={store.storeId}>
                <button
                  type="button"
                  onClick={() => handleSelect(store.storeSlug)}
                  disabled={isSwitching}
                  aria-current={isActive ? "true" : undefined}
                  className="group flex w-full items-center gap-3.5 rounded-[14px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4 text-start transition-colors hover:border-[color:var(--border-strong)] disabled:cursor-wait disabled:opacity-60"
                >
                  <StoreMonogram storeName={store.storeName} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[14.5px] font-semibold text-[color:var(--text-primary)]">
                      {store.storeName}
                    </div>
                    <div className="mt-0.5 truncate font-mono text-[12px] tracking-[0.04em] text-[color:var(--text-muted)]">
                      {store.storeShort}
                    </div>
                  </div>
                  {isActive ? (
                    <span className="inline-flex shrink-0 items-center rounded-full border border-[color:var(--border-light)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--text-muted)]">
                      {t(keys.seller.storeSwitcherCurrent)}
                    </span>
                  ) : null}
                  <span
                    aria-hidden="true"
                    className="shrink-0 text-[color:var(--text-muted)] transition-transform group-hover:translate-x-0.5 rtl:rotate-180"
                  >
                    <ChevronEnd />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {/* Open another store — a seller can own more than one. */}
        {creating ? (
          <div className="mt-3 flex flex-col gap-2.5 rounded-[14px] border border-[color:var(--border-light)] p-4">
            <input
              autoFocus
              value={newStoreName}
              onChange={(event) => setNewStoreName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void handleCreate();
              }}
              placeholder={t(keys.auth.createStorePlaceholder)}
              disabled={isCreating}
              className="h-11 w-full rounded-[10px] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-[14px] text-[color:var(--text-primary)] focus:border-[color:var(--primary-500)] focus:outline-none"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void handleCreate()}
                disabled={isCreating || newStoreName.trim().length < 2}
                className="h-10 flex-1 rounded-[10px] bg-[color:var(--primary-500)] text-[13px] font-semibold text-[color:var(--on-accent)] transition-opacity disabled:opacity-50"
              >
                {t(keys.auth.createStoreSubmit)}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setNewStoreName("");
                  setCreateError(null);
                }}
                disabled={isCreating}
                className="h-10 rounded-[10px] border border-[color:var(--border-light)] px-4 text-[13px] font-semibold text-[color:var(--text-secondary)]"
              >
                {t(keys.auth.createStoreCancel)}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            disabled={isSwitching}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-[14px] border border-dashed border-[color:var(--border-strong)] p-3.5 text-[13.5px] font-semibold text-[color:var(--text-secondary)] transition-colors hover:border-[color:var(--primary-500)] hover:text-[color:var(--text-primary)] disabled:opacity-60"
          >
            <span aria-hidden="true" className="text-[16px] leading-none">
              +
            </span>
            {t(keys.auth.createStoreButton)}
          </button>
        )}

        {isSwitching ? (
          <p className="mt-4 text-[12.5px] text-[color:var(--text-muted)]">
            {t(keys.seller.storeSwitcherLoading)}
          </p>
        ) : null}
        {error || createError ? (
          <p className="mt-4 text-[12.5px] text-[color:var(--error-500)]">
            {error ?? createError}
          </p>
        ) : null}
      </div>
    </main>
  );
}

function StoreMonogram({ storeName }: { storeName: string }) {
  const initial = (storeName || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      aria-hidden="true"
      className="flex h-11 w-11 shrink-0 items-center justify-center font-serif text-[18px] font-bold"
      style={{
        borderRadius: 12,
        background: "linear-gradient(135deg, var(--accent-500), #fbbf24)",
        color: "#0b1220",
        border: "1px solid color-mix(in oklab, var(--accent-500) 60%, black 10%)",
      }}
    >
      {initial}
    </div>
  );
}

function ChevronEnd() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
      aria-hidden="true"
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}
